import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { todayISO, fmtEUR, fmtDate } from "@/lib/format";
import { fmtDuration } from "@/lib/agenda";
import { DESTINATIONS, initialDestination, validatePaymentForm, buildPaymentPayload, applyOwnerDog, applyType, hasLinks, notesLabel } from "@/components/dialogs/paymentDestination";

const TYPES = ["Pagamento", "Rimborso", "Credito cliente", "Rettifica"];
// K9-021: long labels must shrink inside the dialog (ellipsis in trigger, wrapped in list, full text below).
const LONG_TRIGGER = "min-w-0 [&>span]:min-w-0 [&>span]:!block [&>span]:truncate [&>span]:text-left";
const LONG_CONTENT = "max-w-[calc(100vw-2rem)]";
const LONG_ITEM = "whitespace-normal break-words";
const pkgLabel = (p) => `${p.dog_name} · ${fmtEUR(p.total_price)} · residuo ${fmtEUR(p.outstanding)}`;
const sessLabel = (s, isPay) => `${s.service_type} · ${fmtDate(s.date)}${s.start_time ? ` · ${s.start_time}` : ""} · ${fmtDuration(s.duration_minutes) || "—"} · ${s.status}${isPay && s.billable ? ` · residuo ${fmtEUR(s.fin_outstanding)}` : ""}`;

export const PaymentDialog = ({ open, onOpenChange, presetOwnerId, presetDogId, presetPackage, presetSessionId, presetAmount, onSaved }) => {
  const [owners, setOwners] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [packages, setPackages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [methods, setMethods] = useState(["Contanti", "Carta/POS", "Bonifico", "Altro"]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const presetLinks = { package_id: presetPackage?.id || "", session_id: presetSessionId || "", destination: initialDestination({ presetSessionId, presetPackage }) };

  useEffect(() => {
    if (!open) return;
    api.get("/owners").then((r) => setOwners(r.data)).catch(() => {});
    api.get("/dogs").then((r) => setDogs(r.data)).catch(() => {});
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/sessions").then((r) => setSessions(r.data)).catch(() => {});
    api.get("/settings").then((r) => {
      const pm = (r.data.payment_methods || [])
        .map((m) => (typeof m === "string" ? { name: m, active: true } : m))
        .filter((m) => m.active !== false)
        .map((m) => m.name);
      if (pm.length) setMethods(pm);
    }).catch(() => {});
    setForm({
      date: todayISO(), owner_id: presetOwnerId || "", dog_id: presetDogId || "", ...presetLinks,
      amount: (presetAmount != null && presetAmount !== "") ? presetAmount : (presetPackage?.outstanding || ""),
      method: "Contanti", type: "Pagamento", notes: "", _amtTouched: false,
    });
    // eslint-disable-next-line
  }, [open, presetOwnerId, presetDogId, presetPackage, presetSessionId, presetAmount]);

  const isPay = form.type === "Pagamento";
  const dest = isPay ? form.destination : "";
  const dogsForOwner = dogs.filter((d) => !form.owner_id || d.owner_id === form.owner_id);
  const pkgsForOwner = packages.filter((p) => (!form.owner_id || p.owner_id === form.owner_id) && (!form.dog_id || p.dog_id === form.dog_id)
    && (!isPay || p.outstanding > 0 || p.id === presetPackage?.id));
  const sessForOwner = sessions.filter((s) => (!form.owner_id || s.owner_id === form.owner_id) && (!form.dog_id || (s.dog_ids || []).includes(form.dog_id))
    && (!isPay || (s.billable && s.status !== "Annullata" && s.fin_outstanding > 0) || s.id === presetSessionId));
  const selSess = sessions.find((s) => s.id === form.session_id);
  const selPkg = packages.find((p) => p.id === form.package_id);
  const residual = dest === "SESSION" ? (selSess?.billable ? selSess.fin_outstanding : null) : dest === "PACKAGE" ? (selPkg?.outstanding ?? null) : null;

  const setDestination = (v) => setForm((f) => ({ ...f, destination: v, session_id: v === "SESSION" ? f.session_id : "", package_id: v === "PACKAGE" ? f.package_id : "" }));
  const setType = (v) => setForm((f) => applyType(f, v, presetLinks));

  const pickPackage = (v) => setForm((f) => {
    if (v === "none") return { ...f, package_id: "" };
    const pkg = packages.find((p) => p.id === v);
    const next = { ...f, package_id: v, session_id: "", owner_id: pkg?.owner_id || f.owner_id, dog_id: pkg?.dog_id || f.dog_id };
    if (isPay && pkg && pkg.outstanding != null && !f._amtTouched) next.amount = pkg.outstanding;
    return next;
  });
  const pickSession = (v) => setForm((f) => {
    if (v === "none") return { ...f, session_id: "" };
    const sess = sessions.find((x) => x.id === v);
    const next = { ...f, session_id: v, package_id: "" };
    // Only prefill the residual if the user hasn't manually typed an amount yet.
    if (sess && sess.billable && sess.fin_outstanding != null && (!f.amount || !f._amtTouched)) next.amount = sess.fin_outstanding;
    return next;
  });

  const submit = async () => {
    const err = validatePaymentForm(form, residual);
    if (err) return toast.error(err);
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const res = await api.post("/payments", buildPaymentPayload(form));
      toast.success("Pagamento registrato");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  const showPackage = isPay ? dest === "PACKAGE" : hasLinks(form.type);
  const showSession = isPay ? dest === "SESSION" : hasLinks(form.type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto grid-cols-[minmax(0,1fr)]">
        <DialogHeader><DialogTitle className="font-head">Registra pagamento</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Proprietario *</Label>
            <Select value={form.owner_id} onValueChange={(v) => setForm((f) => applyOwnerDog(f, { owner_id: v, dog_id: "" }, { sessions, packages }))} disabled={!!presetOwnerId}>
              <SelectTrigger data-testid="pay-owner"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
              <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome} {o.cognome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Cane (opzionale)</Label>
            <Select value={form.dog_id || "none"} onValueChange={(v) => setForm((f) => applyOwnerDog(f, { dog_id: v === "none" ? "" : v }, { sessions, packages }))} disabled={!form.owner_id}>
              <SelectTrigger data-testid="pay-dog"><SelectValue placeholder="Nessuno" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessuno</SelectItem>
                {dogsForOwner.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Tipo</Label>
              <Select value={form.type} onValueChange={setType}>
                <SelectTrigger data-testid="pay-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Metodo</Label>
              <Select value={form.method} onValueChange={(v) => setForm((f) => ({ ...f, method: v }))}>
                <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>{methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {isPay && (
            <div className="space-y-1" data-testid="pay-destination">
              <Label>Destinazione del pagamento *</Label>
              <div className="grid grid-cols-3 gap-2" role="radiogroup">
                {DESTINATIONS.map((d) => (
                  <Button key={d.value} type="button" size="sm" role="radio" aria-checked={dest === d.value}
                    variant={dest === d.value ? "default" : "outline"} data-testid={`pay-dest-${d.value.toLowerCase()}`}
                    onClick={() => setDestination(d.value)}>{d.label}</Button>
                ))}
              </div>
            </div>
          )}
          {showPackage && (
            <div className="space-y-1">
              <Label>{isPay ? "Pacchetto *" : "Pacchetto collegato (opzionale)"}</Label>
              <Select value={form.package_id || "none"} onValueChange={pickPackage}>
                <SelectTrigger data-testid="pay-package" className={LONG_TRIGGER} title={selPkg ? pkgLabel(selPkg) : undefined}><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent className={LONG_CONTENT}>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {pkgsForOwner.map((p) => (
                    <SelectItem key={p.id} value={p.id} className={LONG_ITEM}>{pkgLabel(p)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selPkg && <p className="text-xs text-muted-foreground break-words" data-testid="pay-package-full">{pkgLabel(selPkg)}</p>}
            </div>
          )}
          {showSession && (
            <div className="space-y-1">
              <Label>{isPay ? "Prestazione *" : "Prestazione collegata (opzionale)"}</Label>
              <Select value={form.session_id || "none"} onValueChange={pickSession}>
                <SelectTrigger data-testid="pay-session" className={LONG_TRIGGER} title={selSess ? sessLabel(selSess, isPay) : undefined}><SelectValue placeholder="Nessuna" /></SelectTrigger>
                <SelectContent className={LONG_CONTENT}>
                  <SelectItem value="none">Nessuna</SelectItem>
                  {sessForOwner.map((s) => <SelectItem key={s.id} value={s.id} className={LONG_ITEM}>{sessLabel(s, isPay)}</SelectItem>)}
                </SelectContent>
              </Select>
              {selSess && <p className="text-xs text-muted-foreground break-words" data-testid="pay-session-full">{sessLabel(selSess, isPay)}</p>}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Data</Label>
              <Input type="date" data-testid="pay-date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Importo (€) *</Label>
              <Input type="number" min="0" step="0.01" data-testid="pay-amount" value={form.amount} onKeyDown={(e) => { if (e.key === "-" || e.key === "e") e.preventDefault(); }} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value, _amtTouched: true }))} />
            </div>
          </div>
          {residual != null && (
            <p className="text-xs text-muted-foreground" data-testid="pay-residual">Residuo da incassare: {fmtEUR(residual)} · puoi registrare anche un pagamento parziale.</p>
          )}
          <div className="space-y-1">
            <Label data-testid="pay-notes-label">{notesLabel(form.type, dest)}</Label>
            <Textarea data-testid="pay-notes" value={form.notes} placeholder={dest === "OTHER" ? "Es. vendita accessorio, quota evento…" : ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
          {dest === "OTHER" && (
            <p className="text-xs text-muted-foreground" data-testid="pay-other-help">L'altro incasso entra nell'incassato ma non riduce il residuo di prestazioni o pacchetti.</p>
          )}
          {form.type === "Rettifica" && (
            <p className="text-xs text-muted-foreground" data-testid="pay-rettifica-help">La rettifica viene registrata nello storico ma non modifica automaticamente incassato, da incassare o valore del pacchetto. La nota / motivo è obbligatoria.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="pay-save" onClick={submit} disabled={saving}>{saving ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
